import React, { useCallback, useEffect, useState } from "react";
import { useFormik } from "formik";
import moment from "moment";
import "./EcwOrderLogs.scss";
import DatePicker from "react-date-picker";
import { Button, Form, Table } from "react-bootstrap";
import { beautifyJSON, handleNumberInput } from "../../utils/commonUtils";
import useEcwOrders from "../../Hooks/useEcwOrders";
import Loading from "../../components/LoadingPage/Loading";
import CustomModal from "../../components/customModal/CustomModal";
import _ from "lodash";

const initialValues = {
  dateFrom: moment().utc().format("YYYY-MM-DDTHH:mm:ss.SSS[Z]"),
  dateTo: moment().utc().format("YYYY-MM-DDTHH:mm:ss.SSS[Z]"),
  noOfrecords: 100,
};

const EcwOrderLogs: React.FC = () => {
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [orderLogs, setOrderLogs] = useState<any[]>([]);
  const { getEcwOrderLogsAPI, getEcwOrderLogsLoading } = useEcwOrders();

  const { values, handleChange, handleSubmit, resetForm } = useFormik({
    initialValues,
    onSubmit: (values) => {
      fetchOrderLogs(values);
    },
  });

  const toggleModal = useCallback(() => {
    setIsOpenModal((prev) => !prev);
  }, []);

  const fetchOrderLogs = async (payload: any) => {
    try {
      const response = await getEcwOrderLogsAPI(payload);
      setOrderLogs(response);
    } catch (error) {
      console.error("Failed to fetch eCW Order Logs:", error);
    }
  };

  useEffect(() => {
    fetchOrderLogs(values);
  }, []);

  const clearAllFilter = useCallback(() => {
    const isValueChanged = !_.isEqual(values, initialValues);
    if (isValueChanged) {
      resetForm();
      fetchOrderLogs(initialValues);
    }
  }, [resetForm, initialValues, values]);

  const handleViewParams = (rowData: any) => {
    const reqString = beautifyJSON(rowData?.requestPayload);
    const resString = beautifyJSON(rowData?.response);

    setSelectedRow({ reqString, resString });
    setIsOpenModal(true);
  };

  return (
    <section className="ecw-order-logs-page-main">
      <div className="listing-title-box">
        <div className="listing-title-info">
          <h2>eCW Order Logs</h2>
          <p>
            Displaying {values.noOfrecords} of {orderLogs.length} Results
          </p>
        </div>
      </div>
      {getEcwOrderLogsLoading && <Loading />}
      <div className="report-filter-wrapper">
        <Form onSubmit={handleSubmit}>
          <div className="report-filter-item-wrapper">
            <div className="filter-item-box">
              <Form.Label className="form-label"># of Records</Form.Label>
              <Form.Control
                min={0}
                type="number"
                id="noOfrecords"
                className="form-control"
                placeholder="No. of Records"
                onChange={(e: any) => {
                  handleChange("noOfrecords")(e.target.value);
                }}
                onInput={handleNumberInput}
                value={values.noOfrecords}
              />
            </div>
            <div className="filter-item-box">
              <Form.Label className="form-label">Start Date</Form.Label>
              <DatePicker
                locale="en-US"
                format="MM/dd/yyyy"
                className="form-control"
                openCalendarOnFocus={false}
                value={values.dateFrom}
                showLeadingZeros={true}
                dayPlaceholder="dd"
                monthPlaceholder="mm"
                yearPlaceholder="yyyy"
                clearIcon={null}
                calendarIcon={<i className="bi bi-calendar"></i>}
                maxDate={values.dateTo ? new Date(values.dateTo) : new Date()}
                onChange={(date: any) => {
                  handleChange({
                    target: {
                      name: "dateFrom",
                      value: moment(date)
                        .utc()
                        .format("YYYY-MM-DDTHH:mm:ss.SSS[Z]"),
                    },
                  });
                }}
              />
            </div>
            <div className="filter-item-box">
              <Form.Label className="form-label">End Date</Form.Label>
              <DatePicker
                locale="en-US"
                format="MM/dd/yyyy"
                className="form-control"
                openCalendarOnFocus={false}
                value={values.dateTo}
                showLeadingZeros={true}
                dayPlaceholder="dd"
                monthPlaceholder="mm"
                yearPlaceholder="yyyy"
                clearIcon={null}
                calendarIcon={<i className="bi bi-calendar"></i>}
                maxDate={new Date()}
                minDate={
                  values.dateFrom ? new Date(values.dateFrom) : new Date()
                }
                onChange={(date: any) => {
                  handleChange({
                    target: {
                      name: "dateTo",
                      value: moment(date)
                        .utc()
                        .format("YYYY-MM-DDTHH:mm:ss.SSS[Z]"),
                    },
                  });
                }}
              />
            </div>
            <div className="filter-item-box clear-all-box">
              <Button type="submit" variant="primary">
                View
              </Button>
              <button
                type="button"
                className="clear-filter-btn"
                onClick={clearAllFilter}
              >
                Clear All
              </button>
            </div>
          </div>
        </Form>
      </div>
      <div className="filter-detail-wrapper">
        <div className="filter-item-detail-wrapper">
          <div className="filter-item-detail-table-wrapper">
            <Table bordered={false} hover={true}>
              <thead>
                <tr>
                  <th>Request Date</th>
                  <th>Order Id</th>
                  <th>Doses</th>
                  <th>CVX Code</th>
                  <th>VFC Code</th>
                  <th>Encounter Date</th>
                  <th>EMR Json Id</th>
                  <th>Encounter Id</th>
                  <th>Vaccine Name</th>
                  <th>Status</th>
                  <th>Is Success</th>
                  <th>Is Administered</th>
                  <th>Request Payload</th>
                  <th>Response</th>
                </tr>
              </thead>
              <tbody>
                {orderLogs.length > 0 ? (
                  orderLogs.map((log, index) => (
                    <tr key={index}>
                      <td>
                        {log?.lastUpdated
                          ? moment(log.lastUpdated).format(
                              "MM/DD/YYYY hh:mm:ss A"
                            )
                          : ""}
                      </td>
                      <td>{log?.immId}</td>
                      <td>{log?.doses}</td>
                      <td>{log?.cvxCode}</td>
                      <td>{log?.vfcCode}</td>
                      <td>
                        {log?.encounterDate
                          ? moment(log.encounterDate).format(
                              "MM/DD/YYYY hh:mm:ss A"
                            )
                          : ""}
                      </td>
                      <td>{log?.emrJsonId}</td>
                      <td>{log?.enCounterId}</td>
                      <td>{log?.vaccineName}</td>
                      <td>
                        <span
                          className={`status-tag ${log?.status === "Administered" ? "active" : "inactive"}`}
                        >
                          {log?.status}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`status-tag ${log?.isSuccess ? "active" : "inactive"}`}
                        >
                          {log?.isSuccess ? "True" : "False"}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`status-tag ${log?.isAdministered ? "active" : "inactive"}`}
                        >
                          {log?.isAdministered ? "True" : "False"}
                        </span>
                      </td>
                      <td
                        className="json-data"
                        onClick={() => handleViewParams(log)}
                      >
                        {log?.requestPayload}
                      </td>
                      <td
                        className="json-data"
                        onClick={() => handleViewParams(log)}
                      >
                        {log?.response}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={13} className="text-center p-3">
                      No ECW Orders found for selected dates.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </div>
      </div>
      {isOpenModal && (
        <CustomModal
          id="param_detail_modal"
          title="Params Details"
          content={
            <>
              <div className="modal--content--wrapper">
                <div className="param-content-box">
                  <h4>Request</h4>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={selectedRow?.reqString}
                  />
                </div>
                <div className="param-content-box">
                  <h4>Result</h4>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={selectedRow?.resString}
                  />
                </div>
              </div>
            </>
          }
          onClose={toggleModal}
        />
      )}
    </section>
  );
};

export default EcwOrderLogs;
